import React, { CSSProperties } from 'react';
import { z } from 'zod';

import { getLuminance } from '@mui/material/styles';
import { Img } from '@react-email/img';
import { useGlobalLinkColor } from '@wp-block-builder/stores/WaypointDocumentStore/useGlobalStyles';
import { useRenderLiquidTemplate } from '@wp-block-builder/stores/WaypointRendererStore';

import defineBlock from '../helpers/defineBlock';

import getNameInitials from './helpers/getNameInitials';

export const AvatarBlockSchema = defineBlock(
  'Avatar',
  z.object({
    imageUrl: z.string().nullish(),
    size: z.number().nullish(),
    shape: z.enum(['square', 'rounded', 'circle']).nullish(),
    fallbackText: z.string().nullish(),
    fallbackColor: z.string().nullish(),
  })
);
export type TAvatarBlock = z.infer<typeof AvatarBlockSchema>;

function getBorderRadius(shape: unknown, size: number): CSSProperties {
  switch (shape) {
    case 'rounded':
      return { borderRadius: size * 0.125 };
    case 'circle':
      return { borderRadius: size };
    case 'square':
    default:
      return {};
  }
}

function getUrlString(val: unknown): null | string {
  if (typeof val !== 'string') {
    return null;
  }
  const str = val.trim();
  if (str.length === 0) {
    return null;
  }
  if (str.startsWith('http://') || str.startsWith('https://')) {
    return str;
  }
  return null;
}

function useBackgroundColor(block: TAvatarBlock) {
  const documentTextColor = useGlobalLinkColor();
  return block.props?.fallbackColor || documentTextColor;
}

type FallbackAvatarImageProps = {
  backgroundColor: string;
  fallbackText: string;
  size: number;
  shape: 'square' | 'rounded' | 'circle';
};
function FallbackAvatarImage({ backgroundColor, fallbackText, size, shape }: FallbackAvatarImageProps) {
  const style: CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'middle',
    textAlign: 'center',
    maxWidth: '100%',
  };
  if (shape === 'rounded') {
    style.borderRadius = size * 0.125;
  }

  const queryString = new URLSearchParams({
    color: getLuminance(backgroundColor) > 0.5 ? '000000' : 'FFFFFF',
    name: getNameInitials(fallbackText),
    background: backgroundColor.replace(/^#/, ''),
    size: `${size}`,
    rounded: shape === 'circle' ? 'true' : 'false',
  });

  const uiAvatarsUrl = `https://d1iiu589g39o6c.cloudfront.net/avatars?${queryString.toString()}`;
  return <Img style={style} alt={fallbackText} src={uiAvatarsUrl} />;
}

type AvatarBlockProps = {
  block: TAvatarBlock;
  skipLiquid: boolean;
};
export default function AvatarBlock({ block, skipLiquid }: AvatarBlockProps) {
  const size = block.props?.size;
  const fallbackText = block.props?.fallbackText ?? '';
  const imageUrl = block.props?.imageUrl ?? '';
  const backgroundColor = useBackgroundColor(block);
  const shape = block.props?.shape ?? 'square';

  const tFallbackText = useRenderLiquidTemplate({ template: fallbackText, skipLiquid });
  const tBaseImageUrl = useRenderLiquidTemplate({ template: imageUrl, skipLiquid });
  const tImageUrl = getUrlString(tBaseImageUrl);

  const tSize = typeof size === 'number' ? size : 16;
  if (tImageUrl) {
    return (
      <Img
        alt={tFallbackText}
        style={{
          objectFit: 'cover',
          height: tSize,
          width: tSize,
          maxWidth: '100%',
          display: 'inline-block',
          verticalAlign: 'middle',
          textAlign: 'center',
          ...getBorderRadius(shape, tSize),
        }}
        src={tImageUrl}
      />
    );
  }

  return (
    <FallbackAvatarImage backgroundColor={backgroundColor} fallbackText={tFallbackText} size={tSize} shape={shape} />
  );
}
